<script setup>
// One step on the check page (S1; canvas AiReview, ScenApply): New / Change /
// Remove, Live now vs After, its own tick for a Remove or a price cut (SE3),
// "Changed since this was drafted" with keep live / use proposal, "This will
// email N members" (SE9), errors, Edit in form and Leave this one out. Text
// only (SE3).
import {computed, ref} from 'vue';
import {canPreviewScoring, competitionPagePath, conflictOpen, draftStepHeading, isCompetitionStep, isNewStep, isRemoveStep, isScoringSystemStep, removeAfterWord, stepRows, tickMissing} from '~/utils/setupAssistant';
import ScoringSystemPreviewButton from '~/components/scoring/ScoringSystemPreviewButton.vue';
import SetupStepTile from '~/components/admin/setup/SetupStepTile.vue';
import {useSetupAssistant} from '~/composables/useSetupAssistant';

const props = defineProps({
  step: {type: Object, required: true},
  /** {currency, names: {id: name}, stepLabels: {stepId: label}} */
  ctx: {type: Object, default: () => ({})},
  readOnly: {type: Boolean, default: false},
  busy: {type: Boolean, default: false},
  /** Applied / undone: no editing. */
  frozen: {type: Boolean, default: false},
  /** X200: the proposal, so a drafted scoring system can open "Preview score input". */
  proposalId: {type: String, default: null},
});
const emit = defineEmits(['tick', 'keep-live', 'edit', 'remove', 'open-page']);

const rows = computed(() => stepRows(props.step, props.ctx));
const isNew = computed(() => isNewStep(props.step));
const isRemove = computed(() => isRemoveStep(props.step));
const showDetails = ref(false);
const canEdit = computed(() => !props.readOnly && !props.frozen);
const conflict = computed(() => conflictOpen(props.step));
const tickNeeded = computed(() => tickMissing(props.step));
const kept = computed(() => !!props.step.keepLive);
const liveSummary = computed(() => rows.value.filter(r => r.before != null).map(r => `${r.label}: ${r.before}`).join(' · '));
// X105: a competition step has no form to open before it exists; a live one opens its own admin page instead.
const competition = computed(() => isCompetitionStep(props.step));
const pagePath = computed(() => competitionPagePath(props.step));
// X132: a club's own scoring system has no form before it exists (the Scoring systems page lists it afterwards): no Edit in form.
const scoringSystem = computed(() => isScoringSystemStep(props.step));
// X200 (Grant: "I would have presumed I would have been able to preview the scoring system like I can elsewhere?"): the same
// preview dialog the scoring system pickers open, fed the system the apply would create.
const {scoringPreview} = useSetupAssistant();
const previewable = computed(() => !!props.proposalId && canPreviewScoring(props.step));
const loadPreview = () => scoringPreview(props.proposalId, props.step.id);
</script>

<template>
  <div class="ssc tl-card" :class="{'ssc--remove': isRemove, 'ssc--conflict': conflict, 'ssc--err': step.errors?.length, 'ssc--kept': kept}">
    <div class="ssc-head">
      <!-- Grant 2026-11-03 (as the app's draft): the format's identity tile, "New box league" as the heading, the name under it -->
      <div class="ssc-title">
        <setup-step-tile :step="step"/>
        <span class="ssc-words">
          <span class="ssc-heading" :class="{'ssc-heading--remove': isRemove}">{{ draftStepHeading(step) }}</span>
          <span class="ssc-name">{{ step.label }}</span>
        </span>
        <span v-if="kept" class="tl-chip">Live value kept</span>
      </div>
      <div class="ssc-actions">
        <v-btn v-if="competition && pagePath" size="small" variant="text" :disabled="busy" append-icon="mdi-open-in-new" @click="emit('open-page', step)">Open its page</v-btn>
        <!-- X115 (R50 H1): a NEW competition step has a form too (name, fee, dates, joining), so a fee or a date is fixed here, not by arguing with the assistant -->
        <v-btn v-else-if="canEdit && !isRemove && !kept && !scoringSystem" size="small" variant="text" :disabled="busy" @click="emit('edit', step)">Edit in form</v-btn>
        <v-btn v-if="canEdit && !kept" size="small" variant="text" color="error" :disabled="busy" @click="emit('remove', step)">Leave this one out</v-btn>
      </div>
    </div>

    <!-- New: the summary, details on request -->
    <template v-if="isNew">
      <div class="ssc-summary">{{ step.summary || 'A new item.' }}</div>
      <div class="ssc-links">
        <button v-if="rows.length" type="button" class="ssc-more" @click="showDetails = !showDetails">{{ showDetails ? 'Hide details' : 'Show details' }}</button>
        <scoring-system-preview-button v-if="previewable" class="ssc-preview" :load="loadPreview"/>
      </div>
      <div v-if="showDetails" class="ssc-rows">
        <div v-for="r in rows" :key="r.key" class="ssc-row">
          <div class="ssc-k">{{ r.label }}</div>
          <div class="ssc-v">{{ r.after }}</div>
        </div>
      </div>
    </template>

    <!-- Change / Remove: Live now vs After -->
    <template v-else>
      <div class="ssc-compare">
        <div class="ssc-col">
          <div class="ssc-col-t">Live now</div>
          <template v-if="isRemove">
            <div class="ssc-v">{{ liveSummary || 'As it is today' }}</div>
          </template>
          <template v-else>
            <div v-for="r in rows" :key="r.key" class="ssc-row">
              <div class="ssc-k">{{ r.label }}</div>
              <div class="ssc-v">{{ r.before }}</div>
            </div>
            <div v-if="!rows.length" class="ssc-v">Nothing changes.</div>
          </template>
        </div>
        <div class="ssc-col ssc-col--after">
          <div class="ssc-col-t">After</div>
          <template v-if="isRemove">
            <div class="ssc-v ssc-v--strong">{{ removeAfterWord(step) }}</div>
          </template>
          <template v-else>
            <div v-for="r in rows" :key="r.key" class="ssc-row">
              <div class="ssc-k">{{ r.label }}</div>
              <div class="ssc-v ssc-v--strong">{{ r.after }}</div>
            </div>
          </template>
          <div v-if="step.summary && !isRemove" class="ssc-summary ssc-summary--after">{{ step.summary }}</div>
        </div>
      </div>
    </template>

    <div v-if="step.emailLine" class="ssc-line ssc-line--mail"><v-icon size="15">mdi-email-outline</v-icon> {{ step.emailLine }}</div>

    <div v-for="(e, i) in step.errors ?? []" :key="'e' + i" class="ssc-line ssc-line--err"><v-icon size="15" color="error">mdi-alert-circle-outline</v-icon> {{ e }}</div>

    <!-- S1: the target changed since drafting -->
    <div v-if="step.conflict && !kept" class="ssc-conflict">
      <div class="ssc-conflict-t"><v-icon size="15" color="warning">mdi-source-branch</v-icon> Changed since this was drafted</div>
      <div class="ssc-conflict-d">Somebody changed this in the forms after the assistant drafted it. Keep the live value, or use the proposal's.</div>
      <div v-if="canEdit" class="ssc-conflict-actions">
        <v-btn size="small" variant="outlined" :disabled="busy" @click="emit('keep-live', step, true)">Keep the live one</v-btn>
        <v-btn size="small" color="primary" :variant="step.ticked ? 'flat' : 'outlined'" :disabled="busy" @click="emit('tick', step, true)">{{ step.ticked ? 'Using the proposal' : "Use the proposal's" }}</v-btn>
      </div>
    </div>
    <div v-else-if="kept && canEdit" class="ssc-conflict-actions">
      <v-btn size="small" variant="text" :disabled="busy" @click="emit('keep-live', step, false)">Use the proposal's instead</v-btn>
    </div>

    <!-- SE3: a Remove or a price cut needs its own tick -->
    <!-- X111 (R47 L4): the words are the checkbox's label, so clicking them ticks it too -->
    <div v-if="step.needsTick && !kept" class="ssc-tick" :class="{'ssc-tick--missing': tickNeeded}">
      <v-checkbox-btn :id="`ssc-tick-${step.id}`" :model-value="!!step.ticked" :disabled="!canEdit || busy" color="primary" density="compact" @update:model-value="emit('tick', step, $event)"/>
      <label :for="`ssc-tick-${step.id}`" class="ssc-tick-words">
        <div class="ssc-tick-t">{{ step.ticked ? 'Ticked' : 'Tick to apply this one' }}</div>
        <div v-if="step.needsTickReason" class="ssc-tick-d">{{ step.needsTickReason }}</div>
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ssc {
  padding: 14px 16px;
  margin-bottom: 10px;

  &--remove { border-color: rgba(var(--v-theme-warning), 0.5); }
  &--conflict { border-color: rgba(var(--v-theme-warning), 0.5); }
  &--err { border-color: rgba(var(--v-theme-error), 0.45); }
  &--kept { opacity: 0.75; }
}

.ssc-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.ssc-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; }
.ssc-words { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.ssc-heading { font-size: 12.5px; font-weight: 700; color: rgb(var(--v-theme-textSubtle)); }
.ssc-heading--remove { color: rgb(var(--v-theme-error)); }
.ssc-name { font-size: 14.5px; font-weight: 800; letter-spacing: -0.2px; word-break: break-word; }
.ssc-actions { display: flex; gap: 2px; flex-wrap: wrap; }

.ssc-summary {
  font-size: 13px;
  line-height: 1.55;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 6px;
  white-space: pre-wrap;
  word-break: break-word;

  &--after { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); }
}

.ssc-more {
  background: none;
  border: 0;
  padding: 0;
  margin-top: 6px;
  color: rgb(var(--v-theme-primary));
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.ssc-links { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ssc-preview { margin-top: 6px; }

.ssc-rows { margin-top: 8px; }

.ssc-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 8px;
  padding: 3px 0;
  font-size: 12.5px;
  border-bottom: 1px dashed rgb(var(--v-theme-borderLight));

  &:last-child { border-bottom: 0; }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
}

.ssc-k { color: rgb(var(--v-theme-textSubtle)); }
.ssc-v { word-break: break-word; white-space: pre-wrap; }
.ssc-v--strong { font-weight: 600; }

.ssc-compare {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-top: 10px;

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ssc-col {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;

  &--after { background: rgb(var(--v-theme-light)); }
}

.ssc-col-t {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 6px;
}

.ssc-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  margin-top: 8px;
  line-height: 1.5;

  &--mail { color: rgb(var(--v-theme-charcoal)); }
  &--err { color: rgb(var(--v-theme-error)); }
}

.ssc-conflict {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-warning), 0.08);
  border: 1px solid rgba(var(--v-theme-warning), 0.35);
}

.ssc-conflict-t { font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 6px; }
.ssc-conflict-d { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); margin-top: 3px; line-height: 1.5; }
.ssc-conflict-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }

.ssc-tick {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 8px;
  border-radius: 10px;
  border: 1px solid rgb(var(--v-theme-borderLight));

  &--missing { border-color: rgba(var(--v-theme-warning), 0.6); background: rgba(var(--v-theme-warning), 0.06); }
}

.ssc-tick-words { cursor: pointer; }
.ssc-tick-t { font-size: 13px; font-weight: 700; padding-top: 6px; }
.ssc-tick-d { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); line-height: 1.5; }
</style>
